import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import React from 'react';
import { tripLog } from '@/lib/tripLog';

interface NetworkGateContextValue {
  isOnline: boolean;
  isReconnecting: boolean;
}

const NetworkGateContext = createContext<NetworkGateContextValue>({ isOnline: true, isReconnecting: false });

export function useNetworkGate() {
  return useContext(NetworkGateContext);
}

const RECONNECTING_MS = 3500;

export function NetworkGateProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const wasOnlineRef = useRef(true);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Debounce timer for collapsing rapid false→true connectivity bounces
  // (Android fires networkStatusChange AND the appStateChange resume
  // re-check independently; a brief false→true bounce between them used to
  // re-trigger the Reconnecting overlay twice).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNativeRef = useRef(false);
  const cancelledRef = useRef(false);
  const listenerRef = useRef<{ remove: () => void } | null>(null);
  const appListenerRef = useRef<{ remove: () => void } | null>(null);

  const handleNetworkChange = (connected: boolean) => {
    if (cancelledRef.current) return;
    const wasOffline = !wasOnlineRef.current;
    tripLog(`[NET] change connected:${connected} wasOffline:${wasOffline}`);

    // Going offline: cancel any pending reconnect debounce and reflect
    // immediately. No debounce for the offline transition so the overlay
    // appears without delay.
    if (!connected) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      wasOnlineRef.current = false;
      setIsOnline(false);
      return;
    }

    // Internet just came back after being offline. Debounce so a rapid
    // false→true bounce collapses into a single reconnect event: only the
    // first connected:true after a connected:false that survives the
    // debounce window triggers the Reconnecting state.
    if (wasOffline) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        if (cancelledRef.current) return;
        wasOnlineRef.current = true;
        setIsOnline(true);
        setIsReconnecting(true);
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          if (!cancelledRef.current) setIsReconnecting(false);
        }, RECONNECTING_MS);
      }, 300);
      return;
    }

    // Already-online connected signal: just keep isOnline true.
    wasOnlineRef.current = true;
    setIsOnline(true);
  };

  useEffect(() => {
    cancelledRef.current = false;

    async function setup() {
      try {
        const { Capacitor } = await import('@capacitor/core');
        isNativeRef.current = Capacitor.isNativePlatform();
      } catch {
        isNativeRef.current = false;
      }

      if (isNativeRef.current) {
        try {
          const { Network } = await import('@capacitor/network');

          // Initial check
          const status = await Network.getStatus();
          if (!cancelledRef.current) {
            wasOnlineRef.current = status.connected;
            setIsOnline(status.connected);
          }

          // Instant listener
          const handle = await Network.addListener('networkStatusChange', (s) => {
            handleNetworkChange(s.connected);
          });
          listenerRef.current = handle;

          // Re-check on app resume
          try {
            const { App } = await import('@capacitor/app');
            const appHandle = await App.addListener('appStateChange', async ({ isActive }) => {
              if (isActive && !cancelledRef.current) {
                try {
                  const fresh = await Network.getStatus();
                  handleNetworkChange(fresh.connected);
                } catch {}
              }
            });
            appListenerRef.current = appHandle;
          } catch {}
        } catch {}
      } else {
        // Web fallback
        wasOnlineRef.current = navigator.onLine;
        setIsOnline(navigator.onLine);

        const goOnline = () => handleNetworkChange(true);
        const goOffline = () => handleNetworkChange(false);

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);

        listenerRef.current = {
          remove: () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
          },
        };
      }
    }

    setup();

    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      listenerRef.current?.remove();
      listenerRef.current = null;
      appListenerRef.current?.remove();
      appListenerRef.current = null;
    };
  }, []);

  return React.createElement(
    NetworkGateContext.Provider,
    { value: { isOnline, isReconnecting } },
    children
  );
}
